// Core Components
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

// Material UI Theme Imports
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Dialog from '@mui/material/Dialog';
import Alert from '@mui/material/Alert';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { Button } from '../global/components/Button';

// Application Specific Imports
import { userLogin } from 'redux/actions';
import logo from 'assets/logo.svg';
import config from '../config';
import { get, size, noop } from 'global/lib/lodash';
import { Row } from 'global/components/row';
import { HelpModal } from './HelpModal';
import { Text } from 'global/components/text';
import { sendAuthCode } from '../redux/actions/userLogin';
import { stopProp } from '../global/utils';
import { consumePostLoginRedirect, peekPostLoginRedirect } from '../global/utils/loginRedirect';
import { handOffToNativeIfNeeded } from '../global/utils/nativeShell';

const appLogoSx = {
  width: '250px',
  height: '150px',
  margin: (theme) => `${theme.spacing(1)} auto`,
  pointerEvents: 'none',
};

const appLoginSx = {
  width: 'auto',
  display: 'block',
  mx: 3,
  textAlign: 'center',
};

const loginFormSx = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-around',
  mt: 1,
};

const loginFormWrapperSx = {
  width: '100%',
  mt: 1,
};

const spacedButtonSx = {
  m: 1,
};

const policyTextSx = {
  display: 'block',
  mt: 2,
};

function LoginPage(props) {
  const [username, changeUsername] = useState('');
  const [password, changePassword] = useState('');
  const [code, setcode] = useState('');
  const [showPassword, changeShowPassword] = useState(false);
  const [state, setState] = useState({
    screen: 0,
    submits: 0,
  });
  const [message, setmessage] = useState(null);
  // PFM-2653: capture the pending post-login redirect once, at mount, BEFORE
  // renderRedirect() calls consumePostLoginRedirect() and clears it. A non-null
  // value means a logged-out user was bounced here from protected (non-hashed)
  // content and will be returned there after login, so we show the banner.
  const [redirectPending] = useState(() => !!peekPostLoginRedirect());
  const loadedTheme = useSelector((state) => get(state, 'view.hasLoadedTheme'));
  const loadedAccount = useSelector((state) => get(state, 'auth.accountSettings'));
  const [modal, setModal] = useState({
    open: false,
    onClose: () => setModal((s) => ({ ...s, open: !s.open })),
  });

  const { loggedIn } = props;

  const renderRedirect = (redirect) => {
    if (redirect && loadedTheme && !!size(loadedAccount)) {
      // PFM-2565: if the user originally tried to deep-link somewhere before logging in,
      // send them there instead of the home page.
      const target = consumePostLoginRedirect() || '/home';
      try {
        if (window.TLM && typeof window.TLM.checkLogin === 'function') {
          window.TLM.checkLogin();
        }
      } catch (err) {}
      if (handOffToNativeIfNeeded(target)) {
        return <Redirect to="/home" />;
      }
      return <Redirect to={target} />;
    }
  };

  const usernameInputChange = (event) => {
    changeUsername(event.target.value);
  };

  const passwordInputChange = (event) => {
    changePassword(event.target.value);
  };

  const textInputReset = (e) => {
    changeUsername('');
    changePassword('');
    changeShowPassword(false);
    e.preventDefault();
  };

  const handleDialogClose = () => {
    setmessage(null);
  };

  const handleLoginClick = async (e) => {
    try {
      stopProp(e);
      const usernameLogin = username !== undefined ? username : false;
      const passwordLogin = password !== undefined ? password : false;
      await props.dispatch(userLogin(usernameLogin, passwordLogin));
    } catch (err) {
      if (get(err, 'response.status') == 403) {
        setState((s) => ({ ...s, screen: 1 }));
      } else {
        setmessage(true);
      }
    }
  };

  const handleClickShowPassword = () => {
    changeShowPassword(!showPassword);
  };

  const back = () => {
    changePassword('');
    setState((s) => ({ ...s, screen: 0, submits: 0 }));
    setcode('');
  };

  const sendCode = async (e) => {
    try {
      stopProp(e);
      await props.dispatch(sendAuthCode(username, code));
      await handleLoginClick(e);
    } catch (err) {
      const text = get(err, 'response.data.message', 'Validation code does not match.');
      setmessage({ title: 'Validation Failed', text });
      setcode('');
    } finally {
      setState((s) => ({ ...s, submits: s.submits + 1 }));
    }
  };

  useEffect(() => {
    if (state.submits >= 10) {
      back();
      setmessage({ title: 'Too many tries', text: 'Please try again' });
    }
  }, [state.submits]);

  const renderAuthCode = () => {
    return (
      <>
        <Box sx={{ margin: '10px auto', maxWidth: 500 }}>
          <Text>An account validation code has been sent to the email associated with your account. Please check your email and enter that code below.</Text>
        </Box>

        <form noValidate autoComplete="off" onSubmit={!code ? noop : sendCode}>
          <TextField
            required
            id="code"
            label="Validation Code"
            className="login-username"
            value={code}
            onChange={(e) => setcode(e.target.value)}
            variant="outlined"
            sx={{ width: '266px' }}
            slotProps={{
              htmlInput: {
                inputMode: 'numeric',
                pattern: '[0-9]*',
                autoComplete: 'one-time-code',
              },
            }}
          />

          <Row center sx={{ marginTop: '10px' }}>
            <Button sx={spacedButtonSx} title="Back" onClick={back} />
            <Button sx={spacedButtonSx} title="Resend Code" onClick={handleLoginClick} />
            <Button sx={spacedButtonSx} type="submit" color="secondary" title="Confirm" disabled={!code} onClick={sendCode} />
          </Row>
        </form>
      </>
    );
  };

  return (
    <Box sx={appLoginSx}>
      <Dialog open={!!message} onClose={handleDialogClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{get(message, 'title', 'Login Attempt Failed')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{get(message, 'text', 'Your login credentials were not accepted.')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} variant="text" autoFocus>
            Oops
          </Button>
        </DialogActions>
      </Dialog>

      {renderRedirect(loggedIn)}

      <section>
        <Box component="img" src={logo} alt="logo" sx={appLogoSx} />
      </section>

      {redirectPending && (
        <Alert severity="info" data-testid="login-redirect-banner" sx={{ maxWidth: 500, margin: '0 auto 8px', textAlign: 'left' }}>
          Please login to view this content
        </Alert>
      )}

      {state.screen === 0 && (
        <>
          <Box component="form" noValidate autoComplete="off" sx={loginFormSx}>
            <Box sx={loginFormWrapperSx}>
              <TextField required id="username" label="Email" className="login-username" value={username} onChange={usernameInputChange} variant="outlined" sx={{ width: '266px' }} />
            </Box>
            <Box sx={loginFormWrapperSx}>
              <TextField
                required
                id="password"
                label="Password"
                className="login-password"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={password}
                sx={{ marginTop: 'inherit' }}
                onChange={passwordInputChange}
                // PFM-2599 QA round 3: MUI v9 dropped the top-level `InputProps`
                // prop on TextField in favor of `slotProps.input`. The v8-style
                // prop falls through to the DOM as `inputprops="[object Object]"`
                // and the endAdornment (the eye icon) silently disappears.
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton aria-label="Toggle password visibility" onClick={handleClickShowPassword} size="large">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
            <Box sx={loginFormWrapperSx}>
              <Button
                type="submit"
                sx={[spacedButtonSx, { width: '266px' }]}
                variant="contained"
                color="primary"
                label="Login"
                data-testid="login-button-login"
                disabled={!username || !password}
                onClick={handleLoginClick}
                title="Login"
              />
            </Box>
            <Box sx={loginFormWrapperSx}>
              <Button sx={spacedButtonSx} label="Clear" data-testid="login-button-clear" color="primary" variant="outlined" onClick={textInputReset} disabled={!username && !password}>
                Clear
              </Button>
              <Button
                sx={spacedButtonSx}
                label="Forgot Password"
                data-testid="login-button-forgot-password"
                variant="outlined"
                color="primary"
                onClick={() => {
                  window.location.replace(`${config.api.baseUrl}password/reset`);
                }}
              >
                Forgot Password
              </Button>
            </Box>
          </Box>
          <Typography variant="caption" sx={policyTextSx}>
            By logging in you agree to the{' '}
            <a data-testid="login-a-privacy" href="/policy">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a data-testid="login-a-terms" href="/policy/terms">
              User Agreement
            </a>
          </Typography>

          <Row center sx={{ marginTop: '16px' }}>
            <Button variant="contained" color="secondary" data-testid="login-button-help" onClick={() => setModal((s) => ({ ...s, open: !s.open }))}>
              Need Help ?
            </Button>
          </Row>
          <HelpModal {...modal} />
        </>
      )}

      {state.screen === 1 && renderAuthCode()}
    </Box>
  );
}

const mapStateToProps = (state) => {
  return {
    loggedIn: state.auth.accessToken,
    loginFailed: state.auth.loginFailed,
  };
};

export default connect(mapStateToProps)(LoginPage);
